import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SnackbarProvider, useSnackbar } from "notistack";
import {
  grantEmployeeAdvance,
  fetchAdvanceGrantHistory,
} from "../store/slices/salarySlice";
import { fetchAllUsers } from "../store/slices/authSlice";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import {
  User,
  Building,
  DollarSign,
  ChevronDown,
  CreditCard,
  Calendar,
  CheckCircle,
  FileText,
  Clock,
  AlertCircle,
  ChevronRight,
  ChevronUp,
  Info,
  Award,
  TrendingUp,
  Zap,
  ShieldCheck,
} from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const SalaryAdvance = () => {
  const dispatch = useDispatch();
  const { users } = useSelector((state) => state.auth);

  const { advanceGrantHistory } = useSelector((state) => state.salary);

  const { activeSociety, theme, gradient } = useSidebarContext();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [repaymentAmount, setRepaymentAmount] = useState("");
  const [advanceHistory, setAdvanceHistory] = useState({});
  const [expandedAdvance, setExpandedAdvance] = useState(null);
  const [animateCard, setAnimateCard] = useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    const savedAdvanceHistory =
      JSON.parse(localStorage.getItem("advanceHistory")) || {};
    setAdvanceHistory(savedAdvanceHistory);
    setIsInitialLoad(false);
  }, []);

  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem("advanceHistory", JSON.stringify(advanceHistory));
    }
  }, [advanceHistory, isInitialLoad]);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  useEffect(() => {
    if (animateCard) {
      const timer = setTimeout(() => {
        setAnimateCard(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [animateCard]);

  useEffect(() => {
    if (
      advanceGrantHistory &&
      advanceGrantHistory.advanceHistory &&
      !isInitialLoad
    ) {
      // Only update if we have data and it's not the initial load
      const newFormattedHistory = {};

      if (selectedEmployee && advanceGrantHistory.advanceHistory.length > 0) {
        // Get existing data for this employee to preserve payment states
        const existingEmployeeAdvances = advanceHistory[selectedEmployee] || [];

        // Create mapping of existing advances by ID for quick lookup
        const existingAdvancesMap = {};
        existingEmployeeAdvances.forEach((advance) => {
          existingAdvancesMap[advance.id] = advance;
        });

        // Initialize array for this employee
        if (!newFormattedHistory[selectedEmployee]) {
          newFormattedHistory[selectedEmployee] = [];
        }

        // Map each transaction from backend, preserving status from existing advances
        advanceGrantHistory.advanceHistory.forEach((transaction) => {
          const existingAdvance = existingAdvancesMap[transaction._id] || {};

          // Keep the existing status if it exists, otherwise use the backend status
          const status =
            existingAdvance.status || transaction.status || "Active";
          // Keep existing payment history if available
          const paymentHistory =
            existingAdvance.paymentHistory || transaction.paymentHistory || [];
          // Keep existing remaining amount to prevent automatic repayment
          const remainingAmount =
            existingAdvance.remainingAmount !== undefined
              ? existingAdvance.remainingAmount
              : transaction.remainingAmount || transaction.amount;

          newFormattedHistory[selectedEmployee].push({
            id: transaction._id,
            date: transaction.transaction_date,
            amount: transaction.amount,
            repaymentAmount: transaction.repaymentAmount || 0,
            remainingAmount: remainingAmount,
            status: status,
            estimatedPayoff: Math.ceil(
              transaction.amount / (transaction.repaymentAmount || 1)
            ),
            paymentHistory: paymentHistory,
            approvedBy: transaction.approvedBy || "Admin",
            purpose: transaction.description || "Salary Advance",
            notes: transaction.notes || "",
            // Keep repaid date if exists
            ...(existingAdvance.repaidDate && {
              repaidDate: existingAdvance.repaidDate,
            }),
          });
        });

        // Update only for the selected employee, preserving other employees' data
        setAdvanceHistory((prevHistory) => ({
          ...prevHistory,
          [selectedEmployee]: newFormattedHistory[selectedEmployee],
        }));
      }
    }
  }, [advanceGrantHistory, selectedEmployee, isInitialLoad, advanceHistory]);

  const societyEmployees = React.useMemo(() => {
    if (!activeSociety || !users) return [];
    return users.filter((user) =>
      activeSociety.users.some(
        (societyUser) =>
          societyUser._id === user._id &&
          user.employeeData &&
          user.role === "Employee"
      )
    );
  }, [activeSociety, users]);

  const calculateRecommendedRepayment = (salary) => {
    return Math.round(salary * 0.2);
  };

  const handleEmployeeChange = (e) => {
    const employeeId = e.target.value;
    setSelectedEmployee(employeeId);

    if (employeeId) {
      const employee = societyEmployees.find(
        (emp) => emp.employeeData._id === employeeId
      );
      if (employee && employee.employeeData.salaryDetails) {
        const salary = employee.employeeData.salaryDetails.salary || 0;
        setRepaymentAmount(calculateRecommendedRepayment(salary).toString());
        dispatch(fetchAdvanceGrantHistory(employeeId));
      }
    } else {
      setRepaymentAmount("");
    }
  };

  const hasAdvanceThisMonth = (employeeId) => {
    if (!advanceHistory[employeeId]) return false;

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    return advanceHistory[employeeId].some((advance) => {
      const advanceDate = new Date(advance.date);
      return (
        advanceDate.getMonth() === currentMonth &&
        advanceDate.getFullYear() === currentYear &&
        advance.status === "Active"
      );
    });
  };

  const toggleExpandAdvance = (employeeId, advanceId) => {
    if (
      expandedAdvance &&
      expandedAdvance.employeeId === employeeId &&
      expandedAdvance.advanceId === advanceId
    ) {
      setExpandedAdvance(null);
    } else {
      setExpandedAdvance({ employeeId, advanceId });
    }
  };

  const handleGrantAdvance = async (e) => {
    e.preventDefault();

    if (!selectedEmployee || !advanceAmount || !repaymentAmount) {
      enqueueSnackbar("Please fill all required fields", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const employee = societyEmployees.find(
      (emp) => emp.employeeData._id === selectedEmployee
    );

    if (!employee) {
      enqueueSnackbar("Invalid employee selected", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const hasActiveAdvance =
      advanceHistory[selectedEmployee] &&
      advanceHistory[selectedEmployee].some((adv) => adv.status === "Active");

    if (hasActiveAdvance) {
      enqueueSnackbar(`${employee.username} already has an active advance`, {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    if (hasAdvanceThisMonth(selectedEmployee)) {
      enqueueSnackbar(
        `${employee.username} has already received an advance this month`,
        {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        }
      );
      return;
    }

    const salary = employee.employeeData.salaryDetails.salary || 0;
    const advanceAmountNum = parseFloat(advanceAmount);
    const repaymentAmountNum = parseFloat(repaymentAmount);

    if (advanceAmountNum > salary * 0.5) {
      enqueueSnackbar("Advance amount cannot exceed 50% of the salary", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    if (repaymentAmountNum <= 0 || repaymentAmountNum > advanceAmountNum) {
      enqueueSnackbar("Invalid repayment amount", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    // Confirmation dialog
    const result = await MySwal.fire({
      title: "Grant Advance Confirmation",
      html: `
        <div class="${theme.tailwind.text9}">
          <p><strong>Society:</strong> ${activeSociety.name}</p>
          <p><strong>Username:</strong> ${employee.username}</p>
          <p><strong>Designation:</strong> ${
            employee.employeeData.designation
          }</p>
          <p><strong>Advance Amount:</strong> PKR${parseFloat(
            advanceAmount
          ).toLocaleString()}</p>
          <p><strong>Monthly Repayment:</strong> PKR${parseFloat(
            repaymentAmount
          ).toLocaleString()}</p>
          <p><strong>Estimated Payoff:</strong> ${Math.ceil(
            advanceAmountNum / repaymentAmountNum
          )} months</p>
          <p>Do you want to proceed with granting this advance?</p>
        </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Grant Advance",
      cancelButtonText: "Cancel",
      background: `${theme.mui.BgLinear}`,
      customClass: {
        title: `${theme.tailwind.text9}`,
        confirmButton: `${theme.tailwind.bg18} ${theme.tailwind.bgHover11} text-white w-36 h-12 p-2 rounded-lg`,
        cancelButton: `bg-gray-600 hover:bg-gray-700 text-white ml-3 w-26 h-12 p-2 rounded-lg`,
      },
      buttonsStyling: false,
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(
          grantEmployeeAdvance({
            employeeId: selectedEmployee,
            advanceAmount: advanceAmountNum,
            repaymentAmount: repaymentAmountNum,
          })
        ).unwrap();

        const now = new Date();
        const newAdvance = {
          id: response._id || `adv-${Date.now()}`,
          date: now.toISOString(),
          amount: advanceAmountNum,
          repaymentAmount: repaymentAmountNum,
          remainingAmount: advanceAmountNum,
          status: "Active",
          estimatedPayoff: Math.ceil(advanceAmountNum / repaymentAmountNum),
          paymentHistory: [],
          approvedBy: "Admin",
          purpose: "Salary Advance",
          notes: "",
        };

        setAdvanceHistory((prev) => ({
          ...prev,
          [selectedEmployee]: [...(prev[selectedEmployee] || []), newAdvance],
        }));

        setAnimateCard(true);

        // Clear form fields
        setSelectedEmployee("");
        setAdvanceAmount("");
        setRepaymentAmount("");

        enqueueSnackbar(
          `Advance granted successfully to ${employee.username}`,
          {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
          }
        );
      } catch (error) {
        console.error("Error granting advance: ", error);
        enqueueSnackbar(error.message || "Failed to grant advance", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      }
    }
  };

  // Process repayment manually
  const handleSimulateRepayment = (employeeId, advanceId) => {
    const employee = societyEmployees.find(
      (emp) => emp.employeeData._id === employeeId
    );
    const updatedHistory = { ...advanceHistory };

    if (!updatedHistory[employeeId]) return;

    const advanceIndex = updatedHistory[employeeId].findIndex(
      (adv) => adv.id === advanceId
    );
    if (advanceIndex === -1) return;

    const advance = updatedHistory[employeeId][advanceIndex];
    const newRemainingAmount =
      advance.remainingAmount - advance.repaymentAmount;

    const paymentDate = new Date();

    const paymentRecord = {
      date: paymentDate.toISOString(),
      amount: advance.repaymentAmount,
      remainingAfter: Math.max(0, newRemainingAmount),
      processedBy: "Admin",
    };

    if (newRemainingAmount <= 0) {
      updatedHistory[employeeId][advanceIndex] = {
        ...advance,
        remainingAmount: 0,
        status: "Repaid",
        repaidDate: paymentDate.toISOString(),
        paymentHistory: [...(advance.paymentHistory || []), paymentRecord],
      };
    } else {
      updatedHistory[employeeId][advanceIndex] = {
        ...advance,
        remainingAmount: newRemainingAmount,
        paymentHistory: [...(advance.paymentHistory || []), paymentRecord],
      };
    }

    setAdvanceHistory(updatedHistory);

    enqueueSnackbar(
      `Repayment of PKR${advance.repaymentAmount.toLocaleString()} processed for ${
        employee.username
      }`,
      {
        variant: "success",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      }
    );
  };

  const toggleExpandEmployee = (employeeId) => {
    if (expandedEmployee === employeeId) {
      setExpandedEmployee(null);
    } else {
      setExpandedEmployee(employeeId);
    }
  };

  if (!activeSociety) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className={`${theme.tailwind.text9} text-xl`}>
          Please select a society to manage salary advances.
        </div>
      </div>
    );
  }

  return (
    <div className={`${gradient} min-h-screen p-6`}>
      <div
        className={`${theme.tailwind.bg6} backdrop-blur-sm rounded-2xl shadow-2xl border ${theme.tailwind.border1} h-full`}
      >
        {/* Header */}
        <div className={`p-6 border-b ${theme.tailwind.border1}`}>
          <div
            className={`relative overflow-hidden rounded-lg p-6 ${gradient} shadow-xl`}
            style={{
              backgroundImage: `
                linear-gradient(to bottom right, ${theme.mui.bg2}),
                url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.04'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
              `,
            }}
          >
            <div className="absolute inset-0 opacity-10">
              <svg
                className="w-full h-full"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <pattern
                    id="grid"
                    width="10"
                    height="10"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.5"
                      className={`${theme.tailwind.text9}`}
                    />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#grid)" />
              </svg>
            </div>

            <div className="relative">
              <div className="flex flex-col space-y-3">
                <div className="flex items-center space-x-3 group">
                  <div
                    className={`p-2 rounded-lg ${theme.tailwind.bg52} transform group-hover:scale-110 transition-transform duration-300`}
                  >
                    <CreditCard className={`w-8 h-8 ${theme.tailwind.text9}`} />
                  </div>
                  <h2
                    className={`text-3xl font-bold ${theme.tailwind.text9} tracking-tight`}
                  >
                    Salary Advance Management
                  </h2>
                </div>

                <div
                  className={`flex items-center space-x-2 ml-14 ${theme.tailwind.text4} ${theme.tailwind.hover1} transition-colors duration-300`}
                >
                  <Building className="w-5 h-5" />
                  <span className="text-lg">{activeSociety.name}</span>
                </div>
              </div>
            </div>

            {/* Policy Info Card */}
            <div
              className={`mt-6 p-3 ${theme.tailwind.bgr30} rounded-lg border ${theme.tailwind.border1} flex items-start`}
            >
              <div className={`p-2 rounded-full ${theme.tailwind.bg52} mr-3`}>
                <Info className={`w-5 h-5 ${theme.tailwind.text9}`} />
              </div>
              <div>
                <h4 className={`${theme.tailwind.text9} font-medium`}>
                  Advance Policy
                </h4>
                <ul
                  className={`${theme.tailwind.text4} text-sm mt-1 space-y-1`}
                >
                  <li className="flex items-center">
                    <ChevronRight
                      className={`w-3 h-3 mr-1 ${theme.tailwind.text9}`}
                    />
                    <span>Maximum advance: 50% of monthly salary</span>
                  </li>
                  <li className="flex items-center">
                    <ChevronRight
                      className={`w-3 h-3 mr-1 ${theme.tailwind.text9}`}
                    />
                    <span>Recommended repayment: 20% of monthly salary</span>
                  </li>
                  <li className="flex items-center">
                    <ChevronRight
                      className={`w-3 h-3 mr-1 ${theme.tailwind.text9}`}
                    />
                    <span>Limit: One advance per month per employee</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          {/* Grant Advance Form */}
          <div
            className={`${theme.tailwind.bg14} p-6 rounded-xl border ${
              theme.tailwind.border1
            } shadow-lg transition-all duration-500 ${
              animateCard
                ? `scale-105 ${theme.tailwind.border27} ${theme.tailwind.shadow4}`
                : ""
            }`}
          >
            <h3
              className={`${theme.tailwind.text9} text-xl font-semibold mb-4 flex items-center`}
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Grant Salary Advance
            </h3>

            <form onSubmit={handleGrantAdvance}>
              {/* Employee Selection */}
              <div className="mb-4">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Select Employee
                </label>
                <div className="relative">
                  <select
                    value={selectedEmployee}
                    onChange={handleEmployeeChange}
                    className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg px-4 py-3 appearance-none focus:ring-2 ${theme.tailwind.focusRing} ${theme.tailwind.focus1} transition-all duration-300`}
                    required
                  >
                    <option value="" className={`${theme.tailwind.bg5}`}>
                      Select an employee
                    </option>
                    {societyEmployees.map((employee) => (
                      <option
                        key={employee._id}
                        value={employee.employeeData._id}
                        className={`${theme.tailwind.bg5}`}
                      >
                        {employee.username} -{" "}
                        {employee.employeeData.designation}
                      </option>
                    ))}
                  </select>
                  <div
                    className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${theme.tailwind.text9}`}
                  >
                    <ChevronDown className="w-5 h-5" />
                  </div>
                </div>
              </div>

              {/* Employee Details (if selected) */}
              {selectedEmployee && (
                <div
                  className={`mb-4 p-4 ${theme.tailwind.bgr31} rounded-lg border ${theme.tailwind.border26} shadow-inner`}
                >
                  {(() => {
                    const employee = societyEmployees.find(
                      (emp) => emp.employeeData._id === selectedEmployee
                    );
                    if (!employee) return null;

                    const salary =
                      employee.employeeData.salaryDetails?.salary || 0;
                    const maxAdvance = salary * 0.5;

                    const hasAdvanceInMonth =
                      hasAdvanceThisMonth(selectedEmployee);

                    return (
                      <>
                        <div
                          className={`flex items-center ${theme.tailwind.text9} mb-3`}
                        >
                          <div
                            className={`p-2 rounded-full ${theme.tailwind.bg52} mr-2`}
                          >
                            <User className="w-4 h-4" />
                          </div>
                          <span className="font-medium">
                            {employee.username}
                          </span>
                        </div>
                        <div className="ml-1">
                          <div
                            className={`grid grid-cols-2 gap-2 ${theme.tailwind.text11} text-sm`}
                          >
                            <div className="flex items-center">
                              <Award className="w-3 h-3 mr-2" />
                              <span>Designation:</span>
                            </div>
                            <div className={`${theme.tailwind.text9}`}>
                              {employee.employeeData.designation}
                            </div>

                            <div className="flex items-center">
                              <DollarSign className="w-3 h-3 mr-2" />
                              <span>Current Salary:</span>
                            </div>
                            <div className={`${theme.tailwind.text9}`}>
                              PKR {salary.toLocaleString()}
                            </div>

                            <div className="flex items-center">
                              <TrendingUp className="w-3 h-3 mr-2" />
                              <span>Max Eligible:</span>
                            </div>
                            <div className={`${theme.tailwind.text9}`}>
                              PKR {maxAdvance.toLocaleString()}
                            </div>
                          </div>

                          {hasAdvanceInMonth && (
                            <div className="mt-3 p-2 bg-red-900/30 rounded border border-red-300/30 flex items-center text-xs">
                              <AlertCircle className="w-4 h-4 mr-2 text-red-300" />
                              <span className="text-red-300">
                                This employee has already received an advance
                                this month
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Advance Amount */}
              <div className="mb-4">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Advance Amount (PKR)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <DollarSign
                      className={`w-5 h-5 ${theme.tailwind.groupDisabled}`}
                    />
                  </div>
                  <input
                    type="number"
                    value={advanceAmount}
                    onChange={(e) => setAdvanceAmount(e.target.value)}
                    placeholder="Enter amount"
                    className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg pl-11 py-3 focus:ring-2 ${theme.tailwind.focusRing} ${theme.tailwind.focus1} transition-all duration-300`}
                    required
                  />
                </div>
              </div>

              {/* Monthly Repayment */}
              <div className="mb-6">
                <label className={`block ${theme.tailwind.text9} mb-2`}>
                  Monthly Repayment (PKR)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Calendar
                      className={`w-5 h-5 ${theme.tailwind.groupDisabled}`}
                    />
                  </div>
                  <input
                    type="number"
                    value={repaymentAmount}
                    onChange={(e) => setRepaymentAmount(e.target.value)}
                    placeholder="Enter repayment amount"
                    className={`w-full ${theme.tailwind.bg5} ${theme.tailwind.text9} border ${theme.tailwind.border1} rounded-lg pl-11 py-3 focus:ring-2 ${theme.tailwind.focusRing} ${theme.tailwind.focus1} transition-all duration-300`}
                    required
                  />
                </div>

                {advanceAmount &&
                  repaymentAmount &&
                  parseFloat(repaymentAmount) > 0 && (
                    <div className={`mt-2 text-sm ${theme.tailwind.text4}`}>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>
                          Estimated payoff time:{" "}
                          {Math.ceil(
                            parseFloat(advanceAmount) /
                              parseFloat(repaymentAmount)
                          )}{" "}
                          months
                        </span>
                      </div>
                    </div>
                  )}
              </div>

              <button
                type="submit"
                className={`w-full ${theme.tailwind.bgGradient14} ${theme.tailwind.groupHoverG5} text-white font-semibold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center`}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Grant Advance</span>
              </button>
            </form>
          </div>

          {/* Advance History */}
          <div
            className={`${theme.tailwind.bg14} p-6 rounded-xl border ${theme.tailwind.border1} shadow-lg`}
          >
            <h3
              className={`${theme.tailwind.text9} text-xl font-semibold mb-4 flex items-center`}
            >
              <FileText className="w-5 h-5 mr-2" />
              Advance History
            </h3>

            {Object.keys(advanceHistory).length === 0 ? (
              <div
                className={`p-8 flex flex-col items-center justify-center ${theme.tailwind.groupDisabled}`}
              >
                <FileText className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-center">No advance history found.</p>
                <p className="text-center text-sm mt-1">
                  Advances granted will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
                {Object.entries(advanceHistory).map(
                  ([employeeId, advances]) => {
                    const employee = societyEmployees.find(
                      (emp) => emp.employeeData._id === employeeId
                    );

                    if (!employee) return null;

                    return (
                      <div
                        key={employeeId}
                        className={`${theme.tailwind.bg3} rounded-lg border ${theme.tailwind.border26} overflow-hidden`}
                      >
                        <div
                          className={`p-4 ${theme.tailwind.bg55} flex items-center justify-between`}
                        >
                          <div
                            className={`flex items-center ${theme.tailwind.text9}`}
                          >
                            <User className="w-5 h-5 mr-2" />
                            <span className="font-medium">
                              {employee.username}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <div
                              className={`text-sm ${theme.tailwind.text20} mr-3`}
                            >
                              {advances.length} advance(s)
                            </div>
                            <button
                              onClick={() => toggleExpandEmployee(employeeId)}
                              className={`${theme.tailwind.text9} ${theme.tailwind.hover2} transition-colors duration-200`}
                            >
                              {expandedEmployee === employeeId ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        {expandedEmployee === employeeId && (
                          <div className={`divide-y ${theme.tailwind.divide2}`}>
                            {advances.map((advance) => (
                              <div key={advance.id} className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <DollarSign
                                      className={`w-4 h-4 mr-2 ${theme.tailwind.text9}`}
                                    />
                                    <span
                                      className={`${theme.tailwind.text9} font-medium`}
                                    >
                                      PKR {advance.amount.toLocaleString()}
                                    </span>
                                  </div>
                                  <div
                                    className={`px-2 py-1 rounded-full text-xs ${
                                      advance.status === "Active"
                                        ? "bg-green-900/30 text-green-300"
                                        : "bg-blue-900/30 text-blue-300"
                                    }`}
                                  >
                                    {advance.status}
                                  </div>
                                </div>

                                <div
                                  className={`grid grid-cols-2 gap-2 mt-2 text-sm ${theme.tailwind.text4}`}
                                >
                                  <div className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    <span>Date:</span>
                                  </div>
                                  <div>
                                    {new Date(
                                      advance.date
                                    ).toLocaleDateString()}
                                  </div>

                                  <div className="flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    <span>Remaining:</span>
                                  </div>
                                  <div>
                                    PKR{" "}
                                    {advance.remainingAmount.toLocaleString()}
                                  </div>

                                  <div className="flex items-center">
                                    <TrendingUp className="w-3 h-3 mr-1" />
                                    <span>Monthly Payment:</span>
                                  </div>
                                  <div>
                                    PKR{" "}
                                    {advance.repaymentAmount.toLocaleString()}
                                  </div>
                                </div>

                                <div className="mt-3 flex items-center justify-between">
                                  <button
                                    className={`text-sm ${theme.tailwind.text9} ${theme.tailwind.hover2} flex items-center transition-colors duration-200`}
                                    onClick={() =>
                                      toggleExpandAdvance(
                                        employeeId,
                                        advance.id
                                      )
                                    }
                                  >
                                    <span>Details</span>
                                    {expandedAdvance &&
                                    expandedAdvance.employeeId === employeeId &&
                                    expandedAdvance.advanceId === advance.id ? (
                                      <ChevronUp className="w-4 h-4 ml-1" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 ml-1" />
                                    )}
                                  </button>

                                  {advance.status === "Active" && (
                                    <button
                                      onClick={() =>
                                        handleSimulateRepayment(
                                          employeeId,
                                          advance.id
                                        )
                                      }
                                      className={`${theme.tailwind.bg57} ${theme.tailwind.text9} ${theme.tailwind.bgHover30} text-xs py-1 px-2 rounded flex items-center transition-colors duration-200`}
                                    >
                                      <ShieldCheck className="w-3 h-3 mr-1" />
                                      <span>Process Payment</span>
                                    </button>
                                  )}
                                </div>

                                {/* Expanded details */}
                                {expandedAdvance &&
                                  expandedAdvance.employeeId === employeeId &&
                                  expandedAdvance.advanceId === advance.id && (
                                    <div
                                      className={`mt-3 pt-3 border-t ${theme.tailwind.border26}`}
                                    >
                                      <div
                                        className={`text-xs ${theme.tailwind.text4} space-y-2`}
                                      >
                                        <div className="flex justify-between">
                                          <span>Approved By:</span>
                                          <span
                                            className={`${theme.tailwind.text9}`}
                                          >
                                            {advance.approvedBy}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Purpose:</span>
                                          <span
                                            className={`${theme.tailwind.text9}`}
                                          >
                                            {advance.purpose}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Estimated Payoff:</span>
                                          <span
                                            className={`${theme.tailwind.text9}`}
                                          >
                                            {advance.estimatedPayoff} months
                                          </span>
                                        </div>

                                        {advance.status === "Repaid" && (
                                          <div className="flex justify-between">
                                            <span>Repaid Date:</span>
                                            <span
                                              className={`${theme.tailwind.text9}`}
                                            >
                                              {new Date(
                                                advance.repaidDate
                                              ).toLocaleDateString()}
                                            </span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Payment history */}
                                      {advance.paymentHistory &&
                                        advance.paymentHistory.length > 0 && (
                                          <div className="mt-3">
                                            <h5
                                              className={`${theme.tailwind.text9} text-sm font-medium mb-2`}
                                            >
                                              Payment History
                                            </h5>
                                            <div
                                              className={`${theme.tailwind.bg55} rounded border ${theme.tailwind.border26} p-2 max-h-40 overflow-y-auto`}
                                            >
                                              <table
                                                className={`w-full text-xs ${theme.tailwind.text4}`}
                                              >
                                                <thead>
                                                  <tr
                                                    className={`text-left border-b ${theme.tailwind.border26}`}
                                                  >
                                                    <th className="pb-1">
                                                      Date
                                                    </th>
                                                    <th className="pb-1">
                                                      Amount
                                                    </th>
                                                    <th className="pb-1">
                                                      Remaining
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {advance.paymentHistory.map(
                                                    (payment, idx) => (
                                                      <tr
                                                        key={idx}
                                                        className={`border-b ${theme.tailwind.border23} last:border-0`}
                                                      >
                                                        <td className="py-1">
                                                          {new Date(
                                                            payment.date
                                                          ).toLocaleDateString()}
                                                        </td>
                                                        <td
                                                          className={`py-1 ${theme.tailwind.text9}`}
                                                        >
                                                          PKR{" "}
                                                          {payment.amount.toLocaleString()}
                                                        </td>
                                                        <td className="py-1">
                                                          PKR{" "}
                                                          {payment.remainingAfter.toLocaleString()}
                                                        </td>
                                                      </tr>
                                                    )
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </div>

        {/* Analytics Dashboard */}
        <div className={`p-6 border-t ${theme.tailwind.border1}`}>
          <h3
            className={`${theme.tailwind.text9} text-xl font-semibold mb-4 flex items-center`}
          >
            <Zap className="w-5 h-5 mr-2" />
            Advance Analytics
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Active Advances */}
            <div
              className={`${theme.tailwind.bg14} p-5 rounded-xl border ${theme.tailwind.border1} shadow-md`}
            >
              <div className="flex items-center justify-between">
                <div className={`${theme.tailwind.text9} font-medium`}>
                  Active Advances
                </div>
                <div className={`p-2 rounded-lg ${theme.tailwind.bg52}`}>
                  <CreditCard className={`w-5 h-5 ${theme.tailwind.text9}`} />
                </div>
              </div>
              <div className="mt-3">
                <div className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                  {Object.values(advanceHistory).reduce(
                    (total, advances) =>
                      total +
                      advances.filter((adv) => adv.status === "Active").length,
                    0
                  )}
                </div>
                <div className={`text-sm ${theme.tailwind.text20} mt-1`}>
                  Currently active advances
                </div>
              </div>
            </div>

            {/* Total Advance Amount */}
            <div
              className={`${theme.tailwind.bg14} p-5 rounded-xl border ${theme.tailwind.border1} shadow-md`}
            >
              <div className="flex items-center justify-between">
                <div className={`${theme.tailwind.text9} font-medium`}>
                  Total Advanced
                </div>
                <div className={`p-2 rounded-lg ${theme.tailwind.bg52}`}>
                  <DollarSign className={`w-5 h-5 ${theme.tailwind.text9}`} />
                </div>
              </div>
              <div className="mt-3">
                <div className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                  PKR{" "}
                  {Object.values(advanceHistory)
                    .reduce(
                      (total, advances) =>
                        total +
                        advances.reduce((sum, adv) => sum + adv.amount, 0),
                      0
                    )
                    .toLocaleString()}
                </div>
                <div className={`text-sm ${theme.tailwind.text20} mt-1`}>
                  Total amount advanced
                </div>
              </div>
            </div>

            {/* Outstanding Balance */}
            <div
              className={`${theme.tailwind.bg14} p-5 rounded-xl border ${theme.tailwind.border1} shadow-md`}
            >
              <div className="flex items-center justify-between">
                <div className={`${theme.tailwind.text9} font-medium`}>
                  Outstanding
                </div>
                <div className={`p-2 rounded-lg ${theme.tailwind.bg52}`}>
                  <TrendingUp className={`w-5 h-5 ${theme.tailwind.text9}`} />
                </div>
              </div>
              <div className="mt-3">
                <div className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                  PKR{" "}
                  {Object.values(advanceHistory)
                    .reduce(
                      (total, advances) =>
                        total +
                        advances.reduce(
                          (sum, adv) => sum + adv.remainingAmount,
                          0
                        ),
                      0
                    )
                    .toLocaleString()}
                </div>
                <div className={`text-sm ${theme.tailwind.text20} mt-1`}>
                  Total outstanding balance
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SalaryAdvanceWithSnackbar = () => (
  <SnackbarProvider maxSnack={3}>
    <SalaryAdvance />
  </SnackbarProvider>
);

export default SalaryAdvanceWithSnackbar;
